import queryString from 'query-string'
import { fetchingAPI, apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

import * as redux from './outboundCampaigns.redux'

export const CALL_LOG_PER_PAGE = 25
export const OPT_OUTS_PER_PAGE = 25

export const fetchCampaigns = () => async (dispatch) => {
  dispatch(redux.setLoading({ campaigns: true }))
  try {
    const response = await fetchingAPI(`${apiService.core}/api/campaigns`, 'GET', dispatch)

    dispatch(redux.setCampaigns(response || []))
  } catch (error) {
    toast.error('Failed to fetch campaigns', { cause: error })
  } finally {
    dispatch(redux.setLoading({ campaigns: false }))
  }
}

export const fetchCampaign = (campaignId) => async (dispatch) => {
  dispatch(redux.setLoading({ campaign: true }))
  dispatch(redux.setCampaign(null))
  try {
    const response = await fetchingAPI(
      `${apiService.core}/api/campaigns/${campaignId}`,
      'GET',
      dispatch
    )

    dispatch(redux.setCampaign(response || null))
    return response || null
  } catch (error) {
    toast.error('Failed to fetch campaign', { cause: error })
    return null
  } finally {
    dispatch(redux.setLoading({ campaign: false }))
  }
}

export const fetchCampaignStats =
  (campaignId, { startDate, endDate } = {}) =>
  async (dispatch, getState) => {
    dispatch(redux.setLoading({ campaignStats: true }))
    dispatch(redux.setCampaignStats(null))
    try {
      const { organizationid: organizationId } = getState().currentUser
      const params = queryString.stringify({
        organization_id: organizationId,
        start_date: startDate,
        end_date: endDate,
      })
      const url = `${apiService.reporting}/api/ai-outbound/campaigns/${campaignId}/stats?${params}`
      const response = await fetchingAPI(url, 'GET', dispatch)
      dispatch(redux.setCampaignStats(response || null))
    } catch (error) {
      toast.error('Failed to fetch campaign stats', { cause: error })
    } finally {
      dispatch(redux.setLoading({ campaignStats: false }))
    }
  }

export const fetchCampaignCallLog =
  (campaignId, { limit = CALL_LOG_PER_PAGE, offset = 0 } = {}) =>
  async (dispatch) => {
    dispatch(redux.setLoading({ campaignCallLog: true }))
    try {
      // The endpoint scopes to the authenticated org via headers and rejects an
      // explicit organization_id param, so only paging params are sent.
      const params = queryString.stringify({ limit, offset })
      const url = `${apiService.reporting}/api/ai-outbound/campaigns/${campaignId}/calls?${params}`
      const response = await fetchingAPI(url, 'GET', dispatch)
      dispatch(redux.setCampaignCallLog(response || { calls: [], total: 0 }))
    } catch (error) {
      toast.error('Failed to fetch campaign call log', { cause: error })
    } finally {
      dispatch(redux.setLoading({ campaignCallLog: false }))
    }
  }

export const clearCampaignCallLog = () => (dispatch) => {
  dispatch(redux.setCampaignCallLog({ calls: [], total: 0 }))
}

export const fetchCampaignOptOuts =
  (campaignId, { limit = OPT_OUTS_PER_PAGE, offset = 0 } = {}) =>
  async (dispatch) => {
    dispatch(redux.setLoading({ campaignOptOuts: true }))
    try {
      const params = queryString.stringify({ campaign_id: campaignId, limit, offset })
      const url = `${apiService.core}/api/opt_outs?${params}`
      const response = await fetchingAPI(url, 'GET', dispatch)
      dispatch(redux.setCampaignOptOuts(response || { opt_outs: [], total: 0 }))
    } catch (error) {
      toast.error('Failed to fetch campaign opt-outs', { cause: error })
    } finally {
      dispatch(redux.setLoading({ campaignOptOuts: false }))
    }
  }

export const clearCampaignOptOuts = () => (dispatch) => {
  dispatch(redux.setCampaignOptOuts({ opt_outs: [], total: 0 }))
}

export const uploadCampaignOptOuts = (campaignId, file) => async (dispatch) => {
  const params = queryString.stringify({ campaign_id: campaignId })
  const formData = new FormData()
  formData.append('file', file)

  dispatch(redux.setLoading({ optOutsUpload: true }))
  try {
    const response = await fetchingAPI(
      `${apiService.core}/api/opt_outs?${params}`,
      'POST',
      dispatch,
      formData,
      undefined,
      undefined,
      undefined,
      undefined,
      false
    )
    return {
      success: true,
      created: response.created,
      skipped: response.skipped,
      invalid: response.invalid,
      errors: response.errors,
    }
  } catch (responseError) {
    const message =
      responseError?.error ||
      responseError?.detail ||
      responseError?.message ||
      'Failed to upload opt-outs'
    return { success: false, errors: Array.isArray(message) ? message : [message] }
  } finally {
    dispatch(redux.setLoading({ optOutsUpload: false }))
  }
}

export const exportCampaignOptOuts = (campaignId, campaignName) => async (dispatch) => {
  dispatch(redux.setLoading({ optOutsExport: true }))
  try {
    const params = queryString.stringify({ campaign_id: campaignId })
    // The endpoint streams a CSV; handleResponse resolves the response body
    // (a ReadableStream) for text/csv, which we collect into a Blob to download.
    const stream = await fetchingAPI(
      `${apiService.core}/api/opt_outs/export?${params}`,
      'GET',
      dispatch
    )
    const blob = await new Response(stream).blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${campaignName} Opt-Outs.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    toast.error('Failed to export opt-outs', { cause: error })
  } finally {
    dispatch(redux.setLoading({ optOutsExport: false }))
  }
}

export const fetchVoiceAgents = () => async (dispatch) => {
  try {
    const response = await fetchingAPI(`${apiService.core}/api/voice_agents`, 'GET', dispatch)

    dispatch(redux.setVoiceAgents(response || []))
  } catch (error) {
    toast.error('Failed to fetch AI agents', { cause: error })
  }
}

export const createCampaign = (campaignData) => async (dispatch) => {
  dispatch(redux.setLoading({ campaigns: true }))
  try {
    await fetchingAPI(
      `${apiService.core}/api/campaigns`,
      'POST',
      dispatch,
      JSON.stringify(campaignData)
    )

    toast.success('Campaign created successfully')
  } catch (error) {
    toast.error(
      `Failed to create campaign.${error?.error_message ? ` ${error.error_message}` : ''}`,
      { cause: error }
    )
    throw error
  } finally {
    dispatch(redux.setLoading({ campaigns: false }))
  }
}

export const updateCampaign = (campaignId, campaignData) => async (dispatch) => {
  try {
    await fetchingAPI(
      `${apiService.core}/api/campaigns/${campaignId}`,
      'PUT',
      dispatch,
      JSON.stringify(campaignData)
    )

    toast.success('Campaign updated')
    dispatch(fetchCampaigns())
  } catch (error) {
    toast.error('Failed to update campaign', { cause: error })
    throw error
  }
}

export const deleteCampaign = (campaignId) => async (dispatch) => {
  try {
    await fetchingAPI(
      `${apiService.core}/api/campaigns/${campaignId}`,
      'PUT',
      dispatch,
      JSON.stringify({ deleted: true })
    )

    toast.success('Campaign deleted')
    dispatch(fetchCampaigns())
  } catch (error) {
    toast.error('Failed to delete campaign', { cause: error })
  }
}
